import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ListingStatusEnum } from "../../../common/commonEnums";
import {
  IBadge,
  IListing,
  IMusic,
  INft,
  IUserProfile,
} from "../../../common/commonTypes";
import ArtistOwnerBadges from "../../../common/components/Badges/ArtistOwnerBadge/ArtistOwnerBadges";
import BidCard from "../../../common/components/BidPanels/BidCard";
import { routes } from "../../../routes";
import Typography from "../../../common/components/Typography/Typography";
import Switch from "../../../common/components/Switch/Switch";
import { musicService } from "../../../api/musicService";
import { ErrorWrapper } from "../../../common/components/styled";
import { fadeIn, getVerticalGap } from "../../../common/styles";
import { getBlockExplorerAddressUrl } from "../../../helpers/transactions";
import ShareBidButton from "../../../common/components/ShareBidButton/ShareBidButton";
import { useSession } from "../../../queries/authentication.query";
import { useListingOwnerQuery } from "../../../queries/listingOwner.query";
import PlaceBidModal from "../../../common/components/Modal/PlaceBidModal";
import { useListingIssuesQuery } from "../../../queries/listingIssues.query";

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const BadgesContainer = styled.div`
  padding-top: 16px;
  animation: ${fadeIn} 0.3s linear;
`;

const NotForSale = styled.div`
  margin: 12px 0;
`;

const StyledShareButton = styled(ShareBidButton)`
  margin: 12px 0;
`;

const HideContainer = styled.div`
  margin: 12px 0;
`;
const HideLeft = styled.div`
  display: flex;
  justify-content: space-between;
`;
const HideInstruction = styled(Typography)`
  max-width: 75%;
`;

const ReceiveAddress = styled.div`
  margin: 32px 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  ${getVerticalGap("2px")};
`;

type Props = {
  music: IMusic;
  listing?: IListing;
  nft?: INft;
  onSuccess: () => void;
};

const BidDetails = ({ music, listing, nft, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [badgesList, setBadgesList] = useState<IBadge[]>([]);
  const { user, isLoggedIn } = useSession();
  const [isHidden, setIsHidden] = useState<boolean>(music?.hidden ?? false);
  const [hasError, setHasError] = useState(false);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);

  const {
    owner,
    isConfirmedOwner,
    isLoaded: isOwnerLoaded,
    ownerAddress,
  } = useListingOwnerQuery(listing, nft);

  const issueDetails = useListingIssuesQuery(listing);

  useEffect(() => {
    if (isOwnerLoaded) {
      const badges: IBadge[] = [
        {
          user: music.artist,
          title: t("artist"),
          redirectUrl: routes.user.url(music.artist.uri),
        },
      ];

      if (
        listing &&
        owner &&
        [
          ListingStatusEnum.minted,
          ListingStatusEnum.minting,
          ListingStatusEnum.sold,
        ].includes(listing.status)
      ) {
        if (isConfirmedOwner) {
          badges.push({
            user: owner,
            title: t("owner"),
            redirectUrl: routes.user.url(owner.uri),
          });
        } else {
          badges.push({
            user: {
              username: ownerAddress,
              approved_artist: false,
            } as IUserProfile,
            title: t("owner"),
            redirectUrl: routes.address(ownerAddress),
          });
        }
      }

      setBadgesList(badges);
    }
  }, [isOwnerLoaded, music.id, listing?.status]);

  async function toggleHidden() {
    try {
      setHasError(false);
      const res = await musicService.updateMusic(music!.id, {
        hidden: !isHidden,
      });
      const newHiddenValue = res.data.hidden;
      if (newHiddenValue === isHidden) {
        setHasError(true);
      }
      setIsHidden(newHiddenValue);
    } catch (e) {
      setHasError(true);
    }
  }

  const renderBadges = () => {
    return badgesList.length > 0 ? (
      <BadgesContainer>
        <ArtistOwnerBadges badges={badgesList} />
      </BadgesContainer>
    ) : null;
  };

  const renderContent = () => {
    if (listing && listing.status === ListingStatusEnum.active) {
      return (
        <>
          <BidCard
            listing={listing}
            nft={nft}
            openBidModal={() => setIsBidModalOpen(true)}
            issueDetails={issueDetails}
          />
          {renderBadges()}
          {listing && isLoggedIn && listing.seller_id === user?.id && (
            <ReceiveAddress>
              <Typography secondary>{t("payout-info")}:</Typography>
              <Typography
                secondary
                truncate
                element="a"
                href={getBlockExplorerAddressUrl(
                  listing.seller_address,
                  listing.deployment.chain
                )}
                target="_blank"
                rel="noreferrer"
              >
                {listing.seller_address}
              </Typography>
            </ReceiveAddress>
          )}
        </>
      );
    }

    if (
      listing &&
      [
        ListingStatusEnum.minted,
        ListingStatusEnum.sold,
        ListingStatusEnum.minting,
      ].includes(listing.status)
    ) {
      return (
        <>
          <BidCard
            listing={listing}
            nft={nft}
            openBidModal={() => setIsBidModalOpen(true)}
          />
          {renderBadges()}
        </>
      );
    }

    return (
      <>
        <Typography heading size="lg" weight="500">
          {music?.title}
        </Typography>
        <NotForSale>
          <Typography size="lg" secondary weight="500">
            {t("not-for-sale")}
          </Typography>
        </NotForSale>
        {music && <StyledShareButton music={music} listingId={listing?.id} />}
        {!listing && isLoggedIn && music?.artist_id === user?.id && (
          <HideContainer>
            <HideLeft>
              <HideInstruction>{t("hide-instruction")}</HideInstruction>
              <Switch toggled={isHidden} onChange={toggleHidden} id="hidden" />
            </HideLeft>
            {hasError && <ErrorWrapper>{t("hide-error")}</ErrorWrapper>}
          </HideContainer>
        )}
        {renderBadges()}
      </>
    );
  };

  return (
    <Root>
      {renderContent()}
      {listing && isBidModalOpen && (
        <PlaceBidModal
          onClose={() => setIsBidModalOpen(false)}
          isOpen
          listing={listing}
          onSuccess={onSuccess}
        />
      )}
    </Root>
  );
};

export default BidDetails;
